/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * "Is the document long enough for scroll-dependent chrome to mean anything?"
 * Shared by the site-wide scroll-progress bar and the floating compare CTA, so
 * both agree on what a long page is and neither shows up on a short one as a
 * stray sliver / stray button.
 *
 * Why the gate needs more than scroll+resize: the consumers sit outside the
 * inner <Routes> in App.tsx, so they never remount on client-side navigation,
 * and the document grows *after* first measurement all over this site —
 * provider data arriving, lazy route chunks resolving under <Suspense>, images
 * and fonts settling, accordions opening. Measuring only on scroll/resize left
 * the gate latched on a stale "short page" reading until the visitor happened
 * to scroll. A ResizeObserver on <body> plus a re-measure per navigation keeps
 * it honest.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Scrollable distance (px beyond the viewport) at which the page counts as
// long, and the lower mark at which it stops counting. The gap between them is
// deliberate: mobile browsers grow `innerHeight` when the URL bar collapses on
// scroll-down and shrink it again on scroll-up, which swings `scrollable` by
// ~60-100px. With a single threshold a page sitting near it flips every time
// the visitor changes direction.
const SHOW_ABOVE = 600;
const HIDE_BELOW = 400;

/** Tracks whether the document is long enough to warrant scroll-dependent chrome. */
export function useIsLongPage(enabled: boolean): boolean {
  // Starts false so SSR/prerendered HTML and the first client render agree; the
  // effect flips it on once there is a real document to measure.
  const [isLong, setIsLong] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!enabled) {
      setIsLong(false);
      return;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setIsLong((wasLong) => (wasLong ? scrollable >= HIDE_BELOW : scrollable >= SHOW_ABOVE));
    };
    // rAF-coalesce bursts so a run of resize/observer callbacks costs one layout read.
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', schedule);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    ro?.observe(document.body);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      ro?.disconnect();
    };
    // pathname: re-measure from scratch on navigation — the consumers outlive
    // every route change, so without it the gate carries the previous page's verdict.
  }, [enabled, pathname]);

  return isLong;
}
